import { CheckCircleIcon, MinusIcon } from '@heroicons/react/24/solid';
import { type ColumnDef, type SortDirection } from '@oysterjs/ui/data-table';
import type { AdminAnalyticsReportFieldsFragment } from '~/api/graphql';

export type ActiveUserRow = AdminAnalyticsReportFieldsFragment['activeUsers'][number];
export type UserActivitySortKey =
  | 'user'
  | 'workspace'
  | 'email'
  | 'document'
  | 'knowledgeBase'
  | 'loggedIn';

const ActionCell = ({ active }: { active: boolean }) =>
  active ? (
    <CheckCircleIcon className="size-5 text-sky-500 dark:text-sky-400" aria-label="Yes" />
  ) : (
    <MinusIcon className="size-5 text-neutral-400 dark:text-neutral-500" aria-label="No" />
  );

export function getUserActivityColumns(): ColumnDef<ActiveUserRow, UserActivitySortKey>[] {
  return [
    {
      id: 'user',
      header: 'User',
      sortKey: 'user',
      renderCell: (user) => (
        <div className="min-w-0">
          <div className="truncate font-medium text-neutral-900 dark:text-white">
            {user.agencyUserName}
          </div>
          <div className="truncate text-xs text-neutral-500 dark:text-neutral-400">
            {user.agencyUserEmail}
          </div>
        </div>
      )
    },
    {
      id: 'workspace',
      header: 'Used workspace',
      sortKey: 'workspace',
      renderCell: (user) => <ActionCell active={user.usedWorkspace} />
    },
    {
      id: 'email',
      header: 'Used email',
      sortKey: 'email',
      renderCell: (user) => <ActionCell active={user.usedEmail} />
    },
    {
      id: 'document',
      header: 'Created document',
      sortKey: 'document',
      renderCell: (user) => <ActionCell active={user.createdArtifact} />
    },
    {
      id: 'knowledgeBase',
      header: 'Added to knowledge base',
      sortKey: 'knowledgeBase',
      renderCell: (user) => <ActionCell active={user.addedToKnowledgeBase} />
    },
    {
      id: 'loggedIn',
      header: 'Logged in',
      sortKey: 'loggedIn',
      renderCell: (user) => <ActionCell active={user.loggedIn} />
    }
  ];
}

function boolRank(value: boolean): number {
  return value ? 1 : 0;
}

export function sortUserActivity(
  data: ActiveUserRow[],
  key: UserActivitySortKey,
  direction: SortDirection
): ActiveUserRow[] {
  const sorted = [...data];
  sorted.sort((a, b) => {
    let cmp = 0;
    switch (key) {
      case 'user':
        cmp = a.agencyUserName.localeCompare(b.agencyUserName, undefined, { sensitivity: 'base' });
        break;
      case 'workspace':
        cmp = boolRank(a.usedWorkspace) - boolRank(b.usedWorkspace);
        break;
      case 'email':
        cmp = boolRank(a.usedEmail) - boolRank(b.usedEmail);
        break;
      case 'document':
        cmp = boolRank(a.createdArtifact) - boolRank(b.createdArtifact);
        break;
      case 'knowledgeBase':
        cmp = boolRank(a.addedToKnowledgeBase) - boolRank(b.addedToKnowledgeBase);
        break;
      case 'loggedIn':
        cmp = boolRank(a.loggedIn) - boolRank(b.loggedIn);
        break;
    }
    if (key !== 'user' && cmp === 0) {
      cmp = a.agencyUserName.localeCompare(b.agencyUserName, undefined, { sensitivity: 'base' });
    }
    return direction === 'desc' ? -cmp : cmp;
  });
  return sorted;
}
